import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLoading, getData, getBalanceData, getHasSignedDocument } from "../selectors";
import { RESET_STATE } from "../actionTypes";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import {
    getIssuePermissionsAction,
    deleteIssuePermissionAction,
    downloadStructureAction,
    getIssuePermissionsBalanceAction,
    generateFileFromIssuePermissionAction,
} from "../actions";
import { createDateFromDDMMYYYY, formatDateYYYYMMDD } from "@icarius-utils/date";
import { getUserData } from "@icarius-app/selectors";
import { openDialogAction } from "@icarius-common/dialog/actions";
import { getLocalizedString } from "@icarius-localization/strings";

const useIssuePermissions = (groupCode) => {
    const [editDialogIsOpen, setEditDialogIsOpen] = useState(false);
    const [createDialogIsOpen, setCreateDialogIsOpen] = useState(false);
    const [balanceDialogIsOpen, setBalanceDialogIsOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null); //seleccionado para editar o borrar
    const [uploadFileDialogIsOpen, setUploadFileDialogIsOpen] = useState(false);

    const dispatch = useDispatch();
    const data = useSelector(getData);
    const isLoading = useSelector(getLoading);
    const balanceData = useSelector(getBalanceData);
    const userData = useSelector(getUserData);
    const hasSignedDocument = useSelector(getHasSignedDocument);

    useEffect(() => {
        dispatch(getIssuePermissionsAction());
        return () => {
            dispatch({ type: RESET_STATE });
        };
    }, [dispatch, groupCode]);

    const handleOpenBalanceDialog = (code) => {
        dispatch(getIssuePermissionsBalanceAction(code)).then((resp) => {
            if (resp?.status === 200) {
                setBalanceDialogIsOpen(true);
            }
        });
    };

    const handleCloseBalanceDialog = () => {
        setBalanceDialogIsOpen(false);
    };

    const handleGenerateFile = (gridRef) => {
        let documents = gridRef.api.getSelectedRows();
        if (documents.length < 1) {
            dispatch(
                openSnackbarAction({ msg: getLocalizedString("mustSelectAtLeastOneRecord"), severity: "warning" })
            );
            return;
        }

        if (hasSignedDocument) {
            dispatch(
                openDialogAction({
                    title: getLocalizedString("atention"),
                    msg: getLocalizedString(
                        "areYouSureYouWantToGeneratePermissionReceiptAlreadyApprovedByYouThatMustBeDigitallySignedByTheCollaboratorAndAfterSignatureReceiptWillBeSentByEmail"
                    ),
                    onConfirm: () => {
                        dispatch(
                            generateFileFromIssuePermissionAction({
                                internalCodes: documents.map((item) => item.internalCode),
                            })
                        );
                    },
                })
            );
            return;
        }

        dispatch(generateFileFromIssuePermissionAction({ internalCodes: documents.map((item) => item.internalCode) }));
    };

    const handleOpenDeleteDialog = (gridRef) => {
        let documents = gridRef.api.getSelectedRows();
        if (documents.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        if (userData.level === "M" && documents[0]["Código de empleado"] === userData.code) {
            dispatch(
                openSnackbarAction({ msg: getLocalizedString("cannotDeleteOwnPermissions"), severity: "warning" })
            );
            return;
        }

        dispatch(
            openDialogAction({
                title: getLocalizedString("atention"),
                msg: getLocalizedString("confirmDeletePermission"),
                onConfirm: () => handleDelete(documents[0]),
            })
        );
    };

    const handleOpenCreateDialog = () => {
        setSelectedDocument(null);
        setCreateDialogIsOpen(true);
    };

    const handleOpenEditDialog = (gridRef) => {
        let documents = gridRef.api.getSelectedRows();
        if (documents.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        if (userData.level === "M" && documents[0]["Código de empleado"] === userData.code) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("cannotEditOwnPermissions"), severity: "warning" }));
            return;
        }

        setSelectedDocument(documents[0]);
        setEditDialogIsOpen(true);
    };

    const handleCloseCreateEditDialog = () => {
        setCreateDialogIsOpen(false);
        setEditDialogIsOpen(false);
        setSelectedDocument(null);
    };

    const handleDelete = (document) => {
        const dataToSend = {
            ...document,
            "Fecha desde": formatDateYYYYMMDD(createDateFromDDMMYYYY(document["Fecha desde"])),
            "Fecha hasta": formatDateYYYYMMDD(createDateFromDDMMYYYY(document["Fecha hasta"])),
            "Fecha desde de aplicación": formatDateYYYYMMDD(
                createDateFromDDMMYYYY(document["Fecha desde de aplicación"])
            ),
            "Fecha hasta de aplicación": formatDateYYYYMMDD(
                createDateFromDDMMYYYY(document["Fecha hasta de aplicación"])
            ),
            "Fecha de retorno": document["Fecha de retorno"]
                ? formatDateYYYYMMDD(createDateFromDDMMYYYY(document["Fecha de retorno"]))
                : null,
        };

        dispatch(deleteIssuePermissionAction(dataToSend));
    };

    const handleDownloadStructure = () => {
        dispatch(downloadStructureAction());
    };

    const handleOpenUploadFileDialogIsOpen = () => {
        setUploadFileDialogIsOpen(true);
    };

    const handleCloseUploadFileDialogIsOpen = () => {
        setUploadFileDialogIsOpen(false);
    };

    const state = {
        editDialogIsOpen,
        createDialogIsOpen,
        selectedDocument,
        uploadFileDialogIsOpen,
        balanceDialogIsOpen,
        balanceData,
    };

    const handlers = {
        handleDelete,
        handleOpenDeleteDialog,
        handleOpenCreateDialog,
        handleOpenEditDialog,
        handleCloseCreateEditDialog,
        handleDownloadStructure,
        handleOpenUploadFileDialogIsOpen,
        handleCloseUploadFileDialogIsOpen,
        handleOpenBalanceDialog,
        handleCloseBalanceDialog,
        handleGenerateFile,
    };

    return {
        data,
        isLoading,
        state,
        handlers,
        hasSignedDocument,
    };
};

export default useIssuePermissions;
